import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";

function UsersTab() {
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [depts, setDepts] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role_id: "", department_id: "" });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/users", { ...form, role_id: form.role_id || null, department_id: form.department_id || null });
      toast.success("User created"); setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", password: "", role_id: "", department_id: "" }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {can("users.create") && <button onClick={() => setOpen(true)} data-testid="settings-new-user-button" className="flex items-center gap-2 rounded-md bg-[#F56B1F] hover:bg-[#D65A10] text-white text-sm font-medium px-4 py-2 transition-colors"><Plus size={16} /> New User</button>}
      </div>
      <div className="bg-white rounded-lg border border-[#D7C6A9] overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 bg-[#FafaF8]"><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Department</th><th className="px-5 py-3">Status</th></tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} data-testid={`settings-user-row-${u.id}`} className="border-t border-[#EEE9DE]">
              <td className="px-5 py-3.5 text-neutral-900 font-medium">{u.first_name} {u.last_name}</td>
              <td className="px-5 py-3.5 text-neutral-600">{u.email}</td>
              <td className="px-5 py-3.5 text-neutral-600">{u.role_name || "—"}</td>
              <td className="px-5 py-3.5 text-neutral-600">{u.department_name || "—"}</td>
              <td className="px-5 py-3.5"><StatusBadge status={u.status} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">New User</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="new-user-first-name" required placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" />
              <input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" />
            </div>
            <input data-testid="new-user-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" />
            <input data-testid="new-user-password" required type="password" placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" />
            <select data-testid="settings-user-role-select" value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} className="w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]"><option value="">Select role…</option>{roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
            <select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]"><option value="">Select department…</option>{depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
            <button data-testid="new-user-submit" disabled={saving} className="w-full flex items-center justify-center gap-2 rounded-md bg-[#F56B1F] hover:bg-[#D65A10] text-white text-sm font-medium py-2.5 transition-colors disabled:opacity-60">{saving && <Loader2 size={16} className="animate-spin" />} Create User</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RolesTab() {
  const [roles, setRoles] = useState([]);
  useEffect(() => { api.get("/roles").then((r) => setRoles(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-white rounded-lg border border-[#D7C6A9] overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 bg-[#FafaF8]"><th className="px-5 py-3">Role</th><th className="px-5 py-3">Permissions</th><th className="px-5 py-3">System</th></tr></thead>
        <tbody>{roles.map((r) => (
          <tr key={r.id} data-testid={`settings-role-row-${r.code}`} className="border-t border-[#EEE9DE]">
            <td className="px-5 py-3.5 text-neutral-900 font-medium">{r.name}</td>
            <td className="px-5 py-3.5 text-neutral-600 mono text-xs">{r.permissions.includes("*") ? "All permissions (*)" : `${r.permissions.length} permissions`}</td>
            <td className="px-5 py-3.5">{r.is_system ? <span className="text-[10px] uppercase tracking-wider bg-[#EEE9DE] px-2 py-0.5 rounded">System</span> : "—"}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function DepartmentsTab() {
  const [depts, setDepts] = useState([]);
  useEffect(() => { api.get("/departments").then((r) => setDepts(r.data)).catch(() => {}); }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {depts.map((d) => (
        <div key={d.id} data-testid={`settings-dept-${d.code}`} className="bg-white rounded-lg border border-[#D7C6A9] p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[#EEE9DE] flex items-center justify-center"><Building2 size={17} className="text-[#F56B1F]" /></div>
          <div><div className="text-sm font-medium text-neutral-900">{d.name}</div><div className="text-[11px] mono text-neutral-400">{d.code}</div></div>
        </div>
      ))}
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/audit-logs").then((r) => setLogs(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <div className="text-sm text-neutral-500 bg-[#EEE9DE] rounded-md px-4 py-3">You do not have permission to view audit logs.</div>;
  return (
    <div className="bg-white rounded-lg border border-[#D7C6A9] overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 bg-[#FafaF8]"><th className="px-5 py-3">Action</th><th className="px-5 py-3">Entity</th><th className="px-5 py-3">When</th></tr></thead>
        <tbody>{logs.map((l) => (
          <tr key={l.id} className="border-t border-[#EEE9DE]"><td className="px-5 py-3 mono text-xs text-neutral-700">{l.action}</td><td className="px-5 py-3 text-neutral-600">{l.entity_type}</td><td className="px-5 py-3 mono text-xs text-neutral-400">{new Date(l.created_at).toLocaleString()}</td></tr>
        ))}
        {logs.length === 0 && <tr><td colSpan={3} className="px-5 py-10 text-center text-sm text-neutral-400">No audit records yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Settings() {
  return (
    <AppShell title="Settings & Admin">
      <Tabs defaultValue="users">
        <TabsList className="bg-[#EEE9DE]">
          <TabsTrigger value="users" data-testid="settings-tab-users" className="data-[state=active]:bg-white data-[state=active]:text-[#F56B1F]">Users</TabsTrigger>
          <TabsTrigger value="roles" data-testid="settings-tab-roles" className="data-[state=active]:bg-white data-[state=active]:text-[#F56B1F]">Roles & Permissions</TabsTrigger>
          <TabsTrigger value="departments" data-testid="settings-tab-departments" className="data-[state=active]:bg-white data-[state=active]:text-[#F56B1F]">Departments</TabsTrigger>
          <TabsTrigger value="audit" data-testid="settings-tab-audit" className="data-[state=active]:bg-white data-[state=active]:text-[#F56B1F]">Audit Log</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="roles" className="mt-4"><RolesTab /></TabsContent>
        <TabsContent value="departments" className="mt-4"><DepartmentsTab /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditTab /></TabsContent>
      </Tabs>
    </AppShell>
  );
}
